import { Tenant, TenantMetadata } from '@/clients/types';
import { Box, Button, Checkbox, Space, Stack, TextInput, Tooltip } from '@mantine/core';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const inputSchema = z.object({
  name: z.string().min(1, '必須項目です。').max(63, '63文字以内で入力してください。'),
  enableMfa: z.boolean(),
  encryptReasoning: z.boolean().optional(),
});

export type Inputs = z.infer<typeof inputSchema>;

type FormProps = {
  tenant: Tenant;
  tenantMetadata: TenantMetadata;
  onSubmit: (data: Inputs) => void;
};

export const MFA_LABEL = '2要素認証を有効にする';
const MFA_TOOLTIP_LABEL =
  '※ 2要素認証を有効にすると、次回ログイン時に認証アプリ（Google Authenticator や Microsoft Authenticatorなど）への登録が必要になります。認証アプリが利用できない場合、ログインできなくなりますのでご注意ください。';
export const REASONING_ENCRYPTION_LABEL = 'Reasoning本文を表示する';
const REASONING_ENCRYPTION_TOOLTIP_LABEL =
  'Reasoning本文表示の有効化は Provider プランでご利用いただけます。ご希望の場合はアップグレードをご検討ください。';
const CHECKBOX_TOOLTIP_PROPS = {
  multiline: true,
  w: 360,
  position: 'right-start',
  offset: 12,
} as const;

export function UpdateTenantForm({ tenant, tenantMetadata, onSubmit }: FormProps) {
  const isProviderTenant = tenantMetadata.contract_plan === 'provider';
  const {
    register,
    handleSubmit,
    formState: { isValid, dirtyFields, errors },
  } = useForm<Inputs>({
    resolver: zodResolver(inputSchema),
    mode: 'onChange',
    defaultValues: {
      name: tenant.spec.name,
      enableMfa: tenant.spec.enable_mfa,
      encryptReasoning: isProviderTenant ? !tenantMetadata.encrypt_reasoning : undefined,
    },
  });
  const isReady = isValid && Object.keys(dirtyFields).length > 0;

  const handleFormSubmit = ({ encryptReasoning, ...data }: Inputs) =>
    onSubmit({
      ...data,
      encryptReasoning: encryptReasoning === undefined ? undefined : !encryptReasoning,
    });

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Stack className="my-3" maw="350px">
        <TextInput label="名前" {...register('name')} error={errors.name?.message} />
        <TextInput type="text" label="ID" disabled value={tenant.keys.id!} />
        <Space />
        <Tooltip {...CHECKBOX_TOOLTIP_PROPS} label={MFA_TOOLTIP_LABEL}>
          <Box w="fit-content">
            <Checkbox size="md" {...register('enableMfa')} label={MFA_LABEL} />
          </Box>
        </Tooltip>
        {isProviderTenant ? (
          <Checkbox
            size="md"
            {...register('encryptReasoning')}
            label={REASONING_ENCRYPTION_LABEL}
          />
        ) : (
          <Tooltip {...CHECKBOX_TOOLTIP_PROPS} label={REASONING_ENCRYPTION_TOOLTIP_LABEL}>
            <Box w="fit-content">
              <Checkbox
                size="md"
                label={REASONING_ENCRYPTION_LABEL}
                checked={!tenantMetadata.encrypt_reasoning}
                readOnly
                disabled
              />
            </Box>
          </Tooltip>
        )}
      </Stack>
      <Button type="submit" size="sm" disabled={!isReady} mt="lg">
        更新
      </Button>
    </form>
  );
}
